import { CloseOutlined, DeleteOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Modal, Popconfirm, Radio, Space, Spin, Table } from "antd";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { tokenVidsLabel } from "@extensions/enums";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import "./messages-clients.css";

const MessagesClients = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editPhone, setEditPhone] = useState("");

  const getData = () => {
    setLoading(true);
    const url = searchText.trim()
      ? `company/clients?searchText=${encodeURIComponent(searchText.trim())}`
      : `company/clients`;
    serverFetch(url)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(getData, []);

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") {
      getData();
    }
  };

  const openEditModal = client => {
    setSelectedClientId(client.id);
    setEditPhone(client.phone ?? "");
    setEditModalVisible(true);
  };

  const handleEdit = () => {
    if (!selectedClientId) return message.warning("Выберите клиента для редактирования");
    const client = items.find(item => item.id === selectedClientId);
    if (client) openEditModal(client);
  };

  const deleteClientById = clientId => {
    setLoading(true);
    serverFetch(`company/Clients/${clientId}`, { method: "DELETE" })
      .then(() => {
        message.success("Клиент удален");
        setSelectedClientId(prev => (prev === clientId ? null : prev));
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка при удалении");
      });
  };

  const handleDelete = () => {
    if (!selectedClientId) return;
    deleteClientById(selectedClientId);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setEditPhone("");
  };

  const handleSaveEdit = () => {
    setLoading(true);
    const client = items.find(item => item.id === selectedClientId);
    serverFetch(`company/Clients/${selectedClientId}`, {
      method: "PUT",
      bodyData: { ...client, phone: editPhone },
    })
      .then(() => {
        message.success("Телефон обновлен");
        closeEditModal();
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка при обновлении");
      });
  };

  const columns = [
    {
      title: "",
      width: 50,
      render: (_, record) => (
        <Radio checked={selectedClientId === record.id} onChange={() => setSelectedClientId(record.id)} />
      ),
    },
    { dataIndex: "id", title: "#", width: 80 },
    { dataIndex: "companyId", title: "ID компании", width: 120 },
    { dataIndex: "phone", title: "Телефон", width: 150 },
    { dataIndex: "vid", title: "Вид", width: 120, render: tokenVidsLabel },
    { dataIndex: "messendgerId", title: "ID в мессенджере", width: 200 },
  ];

  const tableEl = (
    <Table
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey="id"
      size="small"
      scroll={{ x: "max-content" }}
      pagination={false}
    />
  );

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="messages-clients-mobile-list">
        {items.map(record => {
          const vidLabel = tokenVidsLabel(record.vid) ?? "—";
          const mid =
            record.messendgerId != null && String(record.messendgerId).trim() !== ""
              ? String(record.messendgerId)
              : "—";
          return (
            <div
              key={record.id}
              className="messages-clients-mobile-card"
              role="button"
              tabIndex={0}
              onClick={() => openEditModal(record)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openEditModal(record);
                }
              }}
            >
              <div className="messages-clients-mobile-card-corner" onClick={e => e.stopPropagation()}>
                <Popconfirm
                  title="Подтверждение удаления"
                  description="Вы уверены, что хотите удалить этого клиента?"
                  onConfirm={() => deleteClientById(record.id)}
                  okText="Удалить"
                  cancelText="Отмена"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={loading}
                    aria-label="Удалить"
                    className="messages-clients-mobile-delete-btn"
                  />
                </Popconfirm>
              </div>
              <div className="messages-clients-mobile-row1">{record.phone != null && String(record.phone).trim() !== "" ? record.phone : "—"}</div>
              <div className="messages-clients-mobile-row2">
                <span>{vidLabel}</span>
                <span className="messages-clients-mobile-messenger-id">{mid}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );

  return (
    <>
      {isMobile ? (
        <div className="page orders-page--mobile messages-clients-page--mobile">
          <div className="messages-clients-mobile-body">
            <div className="orders-mobile-toolbar">
              <div className="company-backups-mobile-search-row" style={{ marginBottom: 0 }}>
                <Input
                  placeholder="Поиск клиентов..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  allowClear
                  className="company-backups-mobile-search-input"
                />
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={getData}
                  loading={loading}
                  aria-label="Обновить"
                  className="company-backups-mobile-refresh-btn"
                />
              </div>
            </div>
            <div className="orders-mobile-scroll messages-clients-mobile-scroll">{mobileListEl}</div>
          </div>
        </div>
      ) : (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder="Поиск клиентов..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              style={{ width: 300 }}
            />
            <Button type="primary" onClick={getData} loading={loading}>
              Поиск
            </Button>
            <Button onClick={handleEdit} disabled={!selectedClientId || loading}>
              Редактировать
            </Button>
            <Popconfirm
              title="Подтверждение удаления"
              description="Вы уверены, что хотите удалить этого клиента?"
              onConfirm={handleDelete}
              okText="Удалить"
              cancelText="Отмена"
              disabled={!selectedClientId}
            >
              <Button danger disabled={!selectedClientId || loading}>
                Удалить
              </Button>
            </Popconfirm>
          </Space>
          {tableEl}
        </>
      )}

      {editModalVisible &&
        (isMobile ? (
          <div
            className="misoft-modal-overlay company-base-overlay--mobile"
            onClick={e => e.target === e.currentTarget && closeEditModal()}
          >
            <div
              className="misoft-modal-content company-base-sheet--mobile"
              onClick={e => e.stopPropagation()}
            >
              <div className="company-base-sheet-header">
                <span className="company-base-sheet-title">Клиент</span>
                <Space size="small" className="company-base-sheet-actions">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveEdit}
                    loading={loading}
                    aria-label="Сохранить"
                    className="company-base-header-icon-btn"
                  />
                  <Button
                    type="default"
                    icon={<CloseOutlined />}
                    onClick={closeEditModal}
                    aria-label="Закрыть"
                    className="company-base-header-icon-btn"
                  />
                </Space>
              </div>
              <div className="company-base-sheet-body">
                <Form layout="vertical" colon={false} disabled={loading}>
                  <Form.Item label="Телефон">
                    <Input
                      value={editPhone}
                      onChange={e => setEditPhone(e.target.value)}
                      placeholder="Введите телефон"
                    />
                  </Form.Item>
                </Form>
              </div>
            </div>
          </div>
        ) : (
          <Modal
            title="Редактирование телефона клиента"
            open
            onOk={handleSaveEdit}
            onCancel={closeEditModal}
            okText="Сохранить"
            cancelText="Отмена"
            confirmLoading={loading}
          >
            <div style={{ marginTop: 16 }}>
              <label>Телефон:</label>
              <Input
                value={editPhone}
                onChange={e => setEditPhone(e.target.value)}
                placeholder="Введите телефон"
                style={{ marginTop: 8 }}
              />
            </div>
          </Modal>
        ))}
    </>
  );
};

export default MessagesClients;
